/** tm/components/ConsultConfirmModal.tsx — 미정 일정 확정 모달 */
import { useState } from 'react';
import type { Consultation, CandidateSlot, FP } from '../types';
import { C, T, R, Btn, Field } from './ui';
import * as api from '../api';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

interface Props {
  consultation: Consultation;
  onDone: () => void;
  onCancel: () => void;
  onToast: (msg: string) => void;
}

export default function ConsultConfirmModal({ consultation, onDone, onCancel, onToast }: Props) {
  const slots: CandidateSlot[] = Array.isArray(consultation.candidate_slots) ? consultation.candidate_slots : [];
  const [manualMode, setManualMode] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [postConfirm, setPostConfirm] = useState(false);
  const [confirmedDate, setConfirmedDate] = useState('');
  const [confirmedTime, setConfirmedTime] = useState('');
  const [confirmedLocation, setConfirmedLocation] = useState('');
  const [smsPreview, setSmsPreview] = useState(false);
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  function buildSmsText(cDate: string, cTime: string, cLoc: string): string {
    const lines = [
      `[임지도사] ${consultation.company_name || '업체'} 동행상담이 확정되었습니다.`,
      '',
      `일시: ${d10(cDate)} ${cTime || ''}`,
      `장소: ${cLoc || '미정'}`,
    ];
    return lines.join('\n');
  }

  async function handleSmsSend() {
    const phone = consultation.fp_phone || '';
    if (!phone) { onToast('FP 연락처가 없습니다'); return; }
    setSmsSending(true);
    try {
      await api.sendSms({ to: phone, message: smsText, context: 'consult-confirm', company_id: consultation.company_id, reference_id: consultation.id });
      onToast('SMS가 발송되었습니다');
      setSmsPreview(false); setPostConfirm(false);
      onDone();
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : 'SMS 발송 실패'); }
    finally { setSmsSending(false); }
  }

  async function confirmWithSlot(slot: CandidateSlot) {
    setSaving(true);
    try {
      await api.updateConsultation(consultation.id, {
        confirm_status: 'confirmed' as const,
        date: slot.date, time: slot.time, location: slot.location,
        candidate_slots: [],
      });
      setConfirmedDate(slot.date); setConfirmedTime(slot.time || ''); setConfirmedLocation(slot.location || '');
      if (consultation.fp_id) {
        setPostConfirm(true);
      } else {
        onToast('일정이 확정되었습니다');
        onDone();
      }
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '확정 실패');
    } finally { setSaving(false); }
  }

  async function confirmManual() {
    if (!date) { onToast('날짜를 선택하세요'); return; }
    setSaving(true);
    try {
      const finalLoc = location || consultation.location;
      await api.updateConsultation(consultation.id, {
        confirm_status: 'confirmed' as const,
        date, time, location: finalLoc,
        candidate_slots: [],
      });
      setConfirmedDate(date); setConfirmedTime(time); setConfirmedLocation(finalLoc || '');
      if (consultation.fp_id) {
        setPostConfirm(true);
      } else {
        onToast('일정이 확정되었습니다');
        onDone();
      }
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : '확정 실패');
    } finally { setSaving(false); }
  }

  // Post-confirm FP SMS dialog
  if (postConfirm && smsPreview) {
    return (
      <div style={{position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div onClick={() => setSmsPreview(false)} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.4)'}} />
        <div style={{position:'relative', width:420, background:C.white, borderRadius:16, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', overflow:'hidden'}}>
          <div style={{padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:T.lg, fontWeight:700}}>SMS 발송 미리보기</span>
            <button onClick={() => setSmsPreview(false)} style={{width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.md}}>&#x2715;</button>
          </div>
          <div style={{padding:'16px 20px'}}>
            <div style={{fontSize:T.sm, color:C.muted, marginBottom:4}}>수신: {consultation.fp_name || 'FP'} ({consultation.fp_phone || ''})</div>
            <textarea value={smsText} onChange={e => setSmsText(e.target.value)} rows={6}
              style={{width:'100%', padding:'12px', borderRadius:10, border:`1px solid ${C.border}`, fontSize:T.md, resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.6}} />
            <div style={{fontSize:T.xs, color:C.muted, textAlign:'right', marginTop:4}}>{smsText.length}자</div>
          </div>
          <div style={{padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8}}>
            <Btn variant="secondary" onClick={() => setSmsPreview(false)} style={{flex:1}}>취소</Btn>
            <Btn onClick={handleSmsSend} disabled={smsSending} style={{flex:1}}>{smsSending ? '발송 중...' : 'SMS 발송'}</Btn>
          </div>
        </div>
      </div>
    );
  }

  if (postConfirm && !smsPreview) {
    return (
      <div style={{position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div onClick={() => { setPostConfirm(false); onToast('일정이 확정되었습니다'); onDone(); }} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.3)'}} />
        <div style={{position:'relative', padding:24, background:C.white, borderRadius:16, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', width:400, textAlign:'center'}}>
          <div style={{fontSize:32, marginBottom:8}}>&#x2705;</div>
          <div style={{fontSize:T.lg, fontWeight:700, marginBottom:4}}>일정이 확정되었습니다</div>
          <div style={{fontSize:T.md, color:C.muted, marginBottom:16}}>FP에게 SMS로 알리시겠습니까?</div>
          <div style={{display:'flex', gap:8}}>
            <Btn variant="secondary" onClick={() => { setPostConfirm(false); onToast('일정이 확정되었습니다'); onDone(); }} style={{flex:1}}>닫기</Btn>
            <Btn onClick={() => { setSmsText(buildSmsText(confirmedDate, confirmedTime, confirmedLocation)); setSmsPreview(true); }} style={{flex:1}}>FP에게 발송</Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div onClick={onCancel} style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.4)'}} />
      <div style={{position:'relative', width:'100%', maxWidth:560, maxHeight:'92vh', background:C.white, borderRadius:16, display:'flex', flexDirection:'column', boxShadow:'0 8px 32px rgba(0,0,0,0.15)'}}>
        {/* 헤더 */}
        <div style={{padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <span style={{fontSize:T.lg, fontWeight:700}}>📅 일정 확정</span>
          <button onClick={onCancel} style={{width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.md}}>✕</button>
        </div>

        {/* 상담 정보 */}
        <div style={{padding:'12px 20px', background:'#FFFBEB', borderBottom:`1px solid #FDE68A`}}>
          <div style={{fontSize:T.md, fontWeight:700, color:'#92400E'}}>{consultation.company_name || '업체'}</div>
          <div style={{fontSize:T.sm, color:'#B45309', marginTop:2}}>{consultation.fp_name || 'FP 미지정'} · {consultation.topic || '주제 없음'}</div>
        </div>

        <div style={{flex:1, overflowY:'auto', padding:'16px 20px'}}>
          {/* 후보 슬롯 */}
          {slots.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:T.md, fontWeight:700, color:C.sub, marginBottom:8}}>후보 일정 중 선택</div>
              {slots.map((slot, si) => (
                <button key={si} onClick={() => confirmWithSlot(slot)} disabled={saving}
                  style={{width:'100%', padding:'12px 16px', borderRadius:10, border:'2px solid #16A34A44', background:'#F0FDF4', marginBottom:6, cursor:'pointer', display:'flex', alignItems:'center', gap:12, textAlign:'left'}}>
                  <span style={{fontSize:T.xl}}>📅</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:T.md, fontWeight:700, color:'#16A34A'}}>{d10(slot.date)} {slot.time}</div>
                    {slot.location && <div style={{fontSize:T.sm, color:'#166534'}}>📍 {slot.location}</div>}
                  </div>
                  <span style={{fontSize:T.md, fontWeight:700, color:'#16A34A'}}>확정 →</span>
                </button>
              ))}
            </div>
          )}

          {/* 구분선 */}
          {slots.length > 0 && (
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
              <div style={{flex:1, height:1, background:C.border}} />
              <span style={{fontSize:T.sm, color:C.muted}}>또는</span>
              <div style={{flex:1, height:1, background:C.border}} />
            </div>
          )}

          {/* 직접 입력 */}
          <div>
            <button onClick={() => setManualMode(!manualMode)}
              style={{width:'100%', padding:'10px 16px', borderRadius:10, border:`2px dashed ${C.accent}44`, background:manualMode ? C.accentLt : C.white, color:C.accent, fontSize:T.md, fontWeight:600, cursor:'pointer', marginBottom:manualMode ? 12 : 0}}>
              {manualMode ? '직접 입력 취소' : '✏️ 직접 날짜/시간 입력'}
            </button>
            {manualMode && (
              <div style={{padding:14, borderRadius:10, background:C.bg, border:`1px solid ${C.border}`}}>
                <div style={{display:'flex', gap:8}}>
                  <div style={{flex:1}}><Field label="날짜" value={date} type="date" required onChange={setDate} /></div>
                  <div style={{flex:1}}><Field label="시간" value={time} type="time" onChange={setTime} /></div>
                </div>
                <Field label="장소" value={location} placeholder={consultation.location || '장소 입력'} onChange={setLocation} />
                <Btn onClick={confirmManual} disabled={saving || !date} style={{width:'100%', marginTop:8}}>
                  {saving ? '⏳ 확정 중...' : '✅ 이 일정으로 확정'}
                </Btn>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
